import { useState } from "react";
import { Download, CheckCircle, Mail, FileText, ArrowRight, X, Loader2 } from "lucide-react";

const LeadMagnet = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");

  const checklistItems = [
    "Complete document checklist for Myntra, Nykaa & Ajio",
    "Step-by-step onboarding process flowchart",
    "Platform commission rates comparison 2026",
    "Common rejection reasons & how to avoid them",
    "Pro tips from 1000+ successful brand launches",
    "Timeline templates for each platform"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!email || !name) {
      setError("Please fill in all required fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    // Save lead to database (backup)
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          phone: phone || null,
          source: "marketplace-checklist-pdf"
        })
      });
    } catch (err) {
      console.error("Error saving lead:", err);
    }

    // Create WhatsApp message with lead details
    const whatsappMessage = `🔔 NEW PDF DOWNLOAD LEAD

Name: ${name}
Email: ${email}
Phone: ${phone || "Not provided"}
Downloaded: Marketplace Onboarding Checklist`;

    // Simple WhatsApp link
    const whatsappURL = `https://wa.me/919464645491?text=${encodeURIComponent(whatsappMessage)}`;

    // Open WhatsApp in new tab
    window.open(whatsappURL, "_blank");

    setIsSuccess(true);
    setIsSubmitting(false);
  };

  const downloadPDF = () => {
    // Open the actual PDF checklist in a new tab
    window.open("/marketplace-checklist-2026.html", "_blank");
  };

  return (
    <>
      {/* Lead Magnet Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-red-500 via-red-600 to-orange-500 relative overflow-hidden" data-testid="lead-magnet-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-bold mb-6">
                <FileText size={16} />
                FREE DOWNLOAD
              </div>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight">
                Marketplace Onboarding<br />
                <span className="text-yellow-300">Checklist 2026</span>
              </h2>
              
              <p className="text-lg md:text-xl text-white/90 mb-8">
                The complete checklist used by 1000+ brands to get approved on Myntra, Nykaa & Ajio on the first attempt.
              </p>

              {/* Checklist Preview */}
              <div className="space-y-3 mb-8">
                {checklistItems.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4 text-white/80 text-sm">
                <div className="flex items-center gap-2">
                  <Download size={16} />
                  <span>1,200+ downloads</span>
                </div>
                <div className="w-1 h-1 bg-white/50 rounded-full" />
                <div className="flex items-center gap-2">
                  <span>PDF format</span>
                </div>
              </div>
            </div>

            {/* Right Content - Form Card */}
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl">
              {!isSuccess ? (
                <>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                    Get Your Free Checklist
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Enter your details to download instantly
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                        placeholder="Your name"
                        required
                        data-testid="lead-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                          placeholder="you@company.com"
                          required
                          data-testid="lead-email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                        placeholder="+91 98765 43210"
                        data-testid="lead-phone-input"
                      />
                    </div>

                    {error && (
                      <div className="text-red-500 text-sm bg-red-50 px-4 py-2 rounded-lg">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="lead-submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Download size={20} />
                          Download Free Checklist
                        </>
                      )}
                    </button>
                  </form>

                  <p className="text-xs text-gray-500 mt-4 text-center">
                    By downloading, you agree to receive marketing emails from BrandBorn. 
                    You can unsubscribe anytime.
                  </p>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Success! Check Your Email
                  </h3>
                  <p className="text-gray-600 mb-6">
                    We've sent the checklist to {email}. If you don't see it, check your spam folder.
                  </p>
                  <button
                    onClick={downloadPDF}
                    className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-8 py-3 rounded-xl font-bold hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
                    data-testid="lead-download-button"
                  >
                    <Download size={18} />
                    Download Now
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    Want expert help? Call us: <a href="tel:+919464645491" className="text-red-500 font-semibold">+91 94646 45491</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile CTA removed - using floating badges instead */}

      {/* Mobile Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end lg:hidden">
          <div className="bg-white w-full rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto animate-slide-up">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Get Free Checklist</h3>
              <button onClick={() => setShowModal(false)} className="p-2">
                <X size={24} />
              </button>
            </div>
            
            {!isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                  placeholder="Your name *"
                  required
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                  placeholder="Email address *"
                  required
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                  placeholder="Phone (optional)"
                />
                {error && <p className="text-red-500 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 rounded-xl font-bold"
                >
                  {isSubmitting ? "Processing..." : "Download Free Checklist"}
                </button>
              </form>
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h4 className="text-lg font-bold mb-2">Success!</h4>
                <p className="text-gray-600 mb-4">Check your email: {email}</p>
                <button
                  onClick={() => {
                    downloadPDF();
                    setShowModal(false);
                  }}
                  className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-xl font-bold"
                >
                  Download Now
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default LeadMagnet;
